import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

export interface ResponseFormattingSettings {
  // Code Block Settings
  syntaxHighlighting: boolean;
  codeTheme: 'github' | 'monokai' | 'dracula' | 'vs2015';
  
  // Math Settings
  mathRendering: boolean;
  mathEngine: 'katex' | 'mathjax';
  
  // Table Settings
  autoFormatTables: boolean;
  tableStyle: 'default' | 'bordered' | 'striped';
  
  // Link Settings
  autoDetectLinks: boolean;
  openLinksInNewTab: boolean;
  
  // Message Density
  messageDensity: 'compact' | 'comfortable' | 'spacious';
  fontSize: 'small' | 'medium' | 'large';
}

const defaultSettings: ResponseFormattingSettings = {
  syntaxHighlighting: true,
  codeTheme: 'github',
  mathRendering: true,
  mathEngine: 'katex',
  autoFormatTables: true,
  tableStyle: 'default',
  autoDetectLinks: true,
  openLinksInNewTab: true,
  messageDensity: 'comfortable',
  fontSize: 'medium',
};

interface ResponseFormattingContextType {
  settings: ResponseFormattingSettings;
  updateSetting: <K extends keyof ResponseFormattingSettings>(
    key: K,
    value: ResponseFormattingSettings[K]
  ) => void;
  resetToDefaults: () => void;
}

const ResponseFormattingContext = createContext<ResponseFormattingContextType | undefined>(undefined);

export const useResponseFormatting = () => {
  const context = useContext(ResponseFormattingContext);
  if (!context) {
    throw new Error('useResponseFormatting must be used within a ResponseFormattingProvider');
  }
  return context;
};

interface ResponseFormattingProviderProps {
  children: ReactNode;
}

export const ResponseFormattingProvider: React.FC<ResponseFormattingProviderProps> = ({ children }) => {
  const [settings, setSettings] = useState<ResponseFormattingSettings>(defaultSettings);

  // Load settings from localStorage on mount
  useEffect(() => {
    const savedSettings = localStorage.getItem('response-formatting-settings');
    if (savedSettings) {
      try {
        const parsed = JSON.parse(savedSettings);
        setSettings({ ...defaultSettings, ...parsed });
      } catch (error) {
        console.error('Failed to parse saved formatting settings:', error);
      }
    }
  }, []);

  // Save settings to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('response-formatting-settings', JSON.stringify(settings));
  }, [settings]);

  const updateSetting = <K extends keyof ResponseFormattingSettings>(
    key: K,
    value: ResponseFormattingSettings[K]
  ) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  const resetToDefaults = () => {
    setSettings(defaultSettings);
  };

  return (
    <ResponseFormattingContext.Provider value={{ settings, updateSetting, resetToDefaults }}>
      {children}
    </ResponseFormattingContext.Provider>
  );
};